import { getIcon } from "@/shared/Icons";
import { getMarkerRenderLayout } from "@/shared/renderers/renderMarkerIcon";
import type { ComputedMarker } from "@/shared/types/zod";
import type { CollisionBox } from "./collision";
import type { MarkerAnimationSnapshot } from "./markerAnimation";

export interface PreparedMarkerPlacement {
	marker: ComputedMarker;
	screenX: number;
	screenY: number;
	image: HTMLCanvasElement;
	imgWidth: number;
	imgHeight: number;
	cx: number;
	cy: number;
	animation: MarkerAnimationSnapshot;
	placementIndex: number | undefined;
}

export interface MarkerPlacementSnapshot {
	minX: number;
	minY: number;
	maxX: number;
	maxY: number;
}

interface MarkerAnimationContext {
	zoom: number;
	ignoreZoom?: boolean;
	markerVisibility: number;
	textVisibility: number;
	now: number;
}

interface PrepareMarkerPlacementsInput {
	features: ComputedMarker[];
	zoom: number;
	width: number;
	height: number;
	isEditMode?: boolean;
	ignoreZoom?: boolean;
	markerVisibility: number;
	textVisibility: number;
	now: number;
	cullMargin: number;
	getImage: (marker: ComputedMarker) => HTMLCanvasElement | undefined;
	getProjectedPoint: (marker: ComputedMarker) => { x: number; y: number };
	getAnimation: (marker: ComputedMarker, context: MarkerAnimationContext) => MarkerAnimationSnapshot;
	getPlacementIndex: (markerId: string) => number | undefined;
}

interface PrepareMarkerPlacementsResult {
	prepared: PreparedMarkerPlacement[];
	hasAnimatingMarkers: boolean;
}

let latestMarkerPlacements = new Map<string, MarkerPlacementSnapshot>();

export function prepareMarkerPlacements(input: PrepareMarkerPlacementsInput): PrepareMarkerPlacementsResult {
	const prepared: PreparedMarkerPlacement[] = [];
	let hasAnimatingMarkers = false;

	for (const marker of input.features) {
		const excluded = marker.properties.__excluded;

		if (excluded && !input.isEditMode) continue;

		const animation = input.getAnimation(marker, {
			zoom: input.zoom,
			ignoreZoom: input.ignoreZoom,
			markerVisibility: input.markerVisibility,
			textVisibility: input.textVisibility,
			now: input.now,
		});
		if (animation.isAnimating) hasAnimatingMarkers = true;

		const projected = input.getProjectedPoint(marker);
		const screenX = projected.x;
		const screenY = projected.y;
		const image = input.getImage(marker);
		if (!image) continue;

		const icon = getIcon(marker.properties.icon);
		const layout = getMarkerRenderLayout(marker, icon, input.zoom);
		const imgWidth = layout.imageWidth;
		const imgHeight = layout.imageHeight;
		const ox = layout.offsetX;
		const oy = layout.offsetY;
		const cx = screenX + ox;
		const cy = screenY + oy;

		if (
			cx + imgWidth / 2 < -input.cullMargin
			|| cy + imgHeight / 2 < -input.cullMargin
			|| cx - imgWidth / 2 > input.width + input.cullMargin
			|| cy - imgHeight / 2 > input.height + input.cullMargin
		) {
			continue;
		}

		prepared.push({
			marker,
			screenX,
			screenY,
			image,
			imgWidth,
			imgHeight,
			cx,
			cy,
			animation,
			placementIndex: input.getPlacementIndex(marker.id),
		});
	}

	return { prepared, hasAnimatingMarkers };
}

export function getPreparedMarkerCollisionBox(placement: PreparedMarkerPlacement, drawOrder?: number): CollisionBox {
	return {
		minX: placement.cx - placement.imgWidth / 2,
		minY: placement.cy - placement.imgHeight / 2,
		maxX: placement.cx + placement.imgWidth / 2,
		maxY: placement.cy + placement.imgHeight / 2,
		markerId: placement.marker.id,
		drawOrder,
	};
}

export function publishMarkerPlacements(placements: PreparedMarkerPlacement[]) {
	latestMarkerPlacements = new Map(
		placements.map((placement) => {
			const drawBox = getPreparedMarkerCollisionBox(placement);
			return [
				placement.marker.id,
				{
					minX: drawBox.minX,
					minY: drawBox.minY,
					maxX: drawBox.maxX,
					maxY: drawBox.maxY,
				} satisfies MarkerPlacementSnapshot,
			];
		})
	);
}

export function getLatestMarkerPlacement(markerId: string): MarkerPlacementSnapshot | undefined {
	return latestMarkerPlacements.get(markerId);
}